/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-29 07:46:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DisplayText, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { formatNumberInThousand } from 'src/helpers';

import {
  buildTotalQueryKey,
  findExecutionId
} from 'src/helpers/analytics-query-execution-id-helper';
import { QUERY_EXECUTION_KEY, NUMBER_DAYS_ON_TOTAL } from 'src/constants/analytics';

import * as analyticsActions from 'src/stores/actions/analytics-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { formatMoney } from 'src/helpers/money-format';

type Props = WithI18nProps;
class Total extends Component<Props> {
  componentDidMount() {
    this.getTotal();
  }

  componentWillReceiveProps(nextProps) {
    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  processUpdateExecutionId = (props, nextProps) => {
    const { totalQueryExecutionId, totalAllSalesQueryExecutionId } = props;
    const {
      totalQueryExecutionId: totalQueryExecutionIdNext,
      totalAllSalesQueryExecutionId: totalAllSalesQueryExecutionIdNext,
      showOrderRevenue
    } = nextProps;

    // Update execution id
    const queryKey = buildTotalQueryKey();
    if (
      !_.isEmpty(totalQueryExecutionIdNext) &&
      totalQueryExecutionId !== totalQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL,
        executionId: totalQueryExecutionIdNext
      });
    }

    if (
      showOrderRevenue &&
      !_.isEmpty(totalAllSalesQueryExecutionIdNext) &&
      totalAllSalesQueryExecutionId !== totalAllSalesQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_ALL_SALES,
        executionId: totalAllSalesQueryExecutionIdNext
      });
    }
  };

  render() {
    const { totalFilters, totalSearches, totalSales, showOrderRevenue, i18n, moneyFormat } =
      this.props;

    return (
      <div className="BC-Analytics__Total">
        {showOrderRevenue ? (
          <div className="section">
            <DisplayText size="large">
              <span
                className="total"
                dangerouslySetInnerHTML={{
                  __html: formatMoney(totalSales, moneyFormat)
                }}></span>
            </DisplayText>
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.Analytics.Total.total_SubduedText1')}
            </TextStyle>
          </div>
        ) : (
          ''
        )}
        <div className="section">
          <DisplayText size="large">{formatNumberInThousand(totalFilters)}</DisplayText>
          <TextStyle variation="subdued">
            {i18n.translate('AdminHTML.Analytics.Total.total_SubduedText2')}
          </TextStyle>
        </div>
        <div className="section">
          <DisplayText size="large">{formatNumberInThousand(totalSearches)}</DisplayText>
          <TextStyle variation="subdued">
            {i18n.translate('AdminHTML.Analytics.Total.total_SubduedText3')}
          </TextStyle>
        </div>
      </div>
    );
  }

  getTotal = () => {
    const { executionIds, showOrderRevenue } = this.props;

    // Build query key
    const queryKey = buildTotalQueryKey();

    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, QUERY_EXECUTION_KEY.TOTAL, executionIds);
    this.props.getTotal(NUMBER_DAYS_ON_TOTAL, queryExecutionId);

    if (showOrderRevenue) {
      const totalAllSalesQueryExecutionId = findExecutionId(
        queryKey,
        QUERY_EXECUTION_KEY.TOTAL_ALL_SALES,
        executionIds
      );

      this.props.getTotalAllSales(NUMBER_DAYS_ON_TOTAL, totalAllSalesQueryExecutionId);
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getTotal: (recentDays, queryExecutionId) =>
    dispatch(analyticsActions.getTotal(recentDays, queryExecutionId, ownProps.i18n)),
  getTotalAllSales: (recentDays, queryExecutionId) =>
    dispatch(analyticsActions.getTotalAllSales(recentDays, queryExecutionId, ownProps.i18n)),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  const themeSetupV2 = _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false);
  const showRevenueTrackingV1 = _.get(
    state.oauth,
    'tenantApp.adminAppFeatures.showRevenueTrackingV1',
    false
  );
  const moneyFormat = _.get(state.oauth, 'tenantApp.externalInfo.money_format', '');
  return {
    moneyFormat,
    totalFilters: _.get(state.analytics, 'totalFilters', 0),
    totalSearches: _.get(state.analytics, 'totalSearches', 0),
    totalSales: _.get(state.analytics, 'totalSales', 0),

    // Show/hide order revenue by themeSetupV2 and showRevenueTrackingV1
    showOrderRevenue: themeSetupV2 || showRevenueTrackingV1,

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    totalQueryExecutionId: _.get(state.analytics, 'totalQueryExecutionId', ''),
    totalAllSalesQueryExecutionId: _.get(state.analytics, 'totalAllSalesQueryExecutionId', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Total));
